/* eslint-disable react/no-array-index-key */
/* eslint-disable react/no-danger */
import DOMPurify from 'dompurify';
import escapeStringRegexp from 'escape-string-regexp';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  desktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

const Content = styled(Vertical)`
  margin: 0 auto;
  max-width: 800px;

  & > *,
  & > h1,
  & > h2,
  & > h3,
  & > h4,
  & > h5,
  & > hr,
  & > ul {
    @media (${desktopAndAbove}) {
      margin-left: 55px;
      margin-right: 55px;
    }
  }

  & img {
    max-width: 100%;
    height: auto;
  }

  & > p {
    margin-top: 1em;
  }

  & > p,
  & ul li {
    text-rendering: optimizeLegibility;
    word-break: break-word;
    line-height: 28px;
  }

  & > div.embed-youtube {
    max-width: unset !important;
    display: flex;
    justify-content: center;

    & > iframe {
      width: 100% !important;
      height: auto !important;
      aspect-ratio: 640 / 360 !important;
    }
  }

  & > div {
    margin-left: 0;
    margin-right: 0;
  }

  & > figure {
    margin-left: 0;
    margin-right: 0;

    &.wp-block-video,
    &.wp-block-embed-youtube {
      & div.wp-block-embed__wrapper,
      iframe {
        height: auto !important;
        width: 100% !important;
        aspect-ratio: 16 / 9 !important;
        object-fit: unset !important;
      }

      &.portrait {
        & div.wp-block-embed__wrapper,
        iframe {
          aspect-ratio: 9 / 16 !important;
        }
      }
    }

    &.wp-block-image {
      & > img {
        width: 100% !important;
        height: auto !important;
      }
    }

    &.wp-block-embed-twitter {
      display: flex;
      justify-content: right;

      & > div.wp-block-embed__wrapper {
        max-width: 800px;
        display: flex;
        justify-content: center;
      }
    }
  }

  & table {
    font-size: var(--intermediate-unit);
    line-height: var(--lh-14);
    @media ${tabletAndAbove} {
      font-size: var(--double-unit);
    }

    td {
      border: 1px solid var(--c-nd-200);
      padding: var(--half-unit);
    }

    img {
      height: initial;
      width: initial;
    }
  }

  & > div.twitter-tweet {
    display: flex;
    max-width: unset !important;
    justify-content: center;

    & iframe {
      max-width: 500px !important;
      border-radius: 14px;
    }
  }

  & .is-content-justification-center {
    justify-content: center;
  }

  & .is-layout-flex {
    display: flex;
  }

  & .wp-block-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--unit);
    height: 60px;
    padding: 0px 20px;
    box-shadow: none;
    color: var(--c-white);
    border: none;
    background-color: var(--c-blue-400);
    border-radius: 2em !important;

    &,
    a,
    span {
      text-decoration: none;
      letter-spacing: 0;
      white-space: nowrap;
      font-family: var(--sorare-font);
      font-weight: var(--t-bold);
      font-style: normal;
    }
    &:hover {
      background: linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1))
        var(--c-blue-400);
      text-decoration: none;
    }
  }
`;

export const ClassicBlock = ({
  data,
  postImageUrl,
}: {
  data: string;
  postImageUrl: string | null | undefined;
}) => {
  let html = DOMPurify.sanitize(data, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'],
  });

  if (postImageUrl) {
    html = html.replace(
      new RegExp(
        `^<figure.*?><img .*?src="${escapeStringRegexp(
          postImageUrl
        )}.*?</figure>`
      ),
      ''
    );
  }

  html = html.replaceAll(
    /<p><span .*?class="embed-youtube">(.+?)<\/span>(.*?)<\/p>/g,
    '<div class="embed-youtube">$1</div>$2'
  );

  return <Content dangerouslySetInnerHTML={{ __html: html }} />;
};
